import SubHeaders from '@/components/Headers/SubHeaders'
import useTabs from '@/hooks/useTabs'
import AddIcons from '@/icons/AddIcons'
import FileIcons from '@/icons/FileIcons'
import UploadIcons from '@/icons/UploadIcons'
import { paths } from '@/utils/path'
import { Camera } from 'lucide-react'
import { useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Tabs } from 'vnetwork-ui'
import {
    getCustomerCounts,
    getCustomerImportTemplate,
    importCustomersFromExcel,
} from '../../services'
import { tabs } from './tab'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import usePermissionsStore from '@/stores/usePermissionsStore'

const List = () => {
    const { t } = useTranslation()
    const { isAdmin, canViewLeadOriginCustomers } = usePermissionsStore()
    const [searchParams] = useSearchParams()

    const { data: countsData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.CUSTOMERS,
                'counts',
                searchParams.get('q'),
                searchParams.get('filters'),
            ],
            queryFn: () =>
                getCustomerCounts({
                    search: searchParams.get('q') || undefined,
                    filters: searchParams.get('filters') || undefined,
                }),
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const memoizedTabs = useMemo(
        () => tabs({ counts: countsData?.result, t, isAdmin }),
        [countsData?.result, t, isAdmin]
    )

    const { TabComponent, TabProps } = useTabs({
        tabs: memoizedTabs,
    })
    const navigate = useNavigate()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleDownloadTemplate = async () => {
        try {
            const res = await getCustomerImportTemplate()
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'customer_import_template.xlsx'
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
            toast.success(t('download-template-success'))
        } catch (error) {
            toast.error(t('download-template-error'))
            console.error('Download template error:', error)
        }
    }

    const handleImportCustomer = () => {
        fileInputRef.current?.click()
    }

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        if (!file) return

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error(t('invalid-file-type'))
            return
        }

        try {
            toast.loading(t('importing-customers'))
            const result = await importCustomersFromExcel(file)
            toast.dismiss()
            toast.success(result.message)

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        } catch (error: any) {
            toast.dismiss()
            toast.error(
                t('import-customers-error', {
                    message: error?.response?.data?.message,
                })
            )

            // Reset file input even on error
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    return (
        <div>
            <SubHeaders
                // Marketing (chỉ xem KH từ lead): ẩn scan card / template / import / thêm KH
                actions={
                    canViewLeadOriginCustomers
                        ? []
                        : [
                              {
                                  icon: <Camera className="w-4 h-4" />,
                                  label: t('scan_card.title', {
                                      ns: 'customer',
                                  }),
                                  variant: 'outlined',
                                  onClick() {
                                      navigate(paths.customerScanCards)
                                  },
                              },
                              {
                                  icon: <FileIcons />,
                                  label: t('import-template'),
                                  onClick() {
                                      handleDownloadTemplate()
                                  },
                                  'data-tour': 'download-template-btn',
                              },
                              {
                                  icon: <UploadIcons />,
                                  label: t('import-customer'),
                                  variant: 'outlined',
                                  onClick() {
                                      handleImportCustomer()
                                  },
                                  'data-tour': 'import-customer-btn',
                              },
                              {
                                  icon: <AddIcons />,
                                  label: t('add-customer'),
                                  onClick() {
                                      navigate(paths.customerCreate)
                                  },
                                  'data-tour': 'create-customer-btn',
                              },
                          ]
                }
                breadcrumbs={[
                    {
                        path: paths.customersList,
                        title: t('customer_manager'),
                    },
                ]}
            />

            <div data-tour="customer-list">
                <Tabs {...TabProps} />
                {TabComponent}
            </div>

            {/* Hidden file input for import */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
            />
        </div>
    )
}

export default List
